import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * One plain list of versions, shared by every version panel.
 *
 * Each row: label, an optional "In production" marker, relative time, author
 * and an optional note. No boxes, hairline dividers only. Row actions stay in
 * the DOM but are invisible until the row is hovered or focused, so they are
 * always keyboard reachable.
 *
 *   items: [{
 *     id, label, live, time, author, note, meta, active, extra,
 *     actions: [{ key, label, onClick, disabled, busy, title }],
 *   }]
 *
 * `meta` and `extra` are optional nodes (a hash, a re-scoring status).
 */
export default function VersionList({ items, empty = 'No versions yet.', liveLabel = 'In production', onSelect }) {
  if (!items.length) {
    return <p className="px-4 py-6 text-[12px] text-[color:var(--text-muted)]">{empty}</p>;
  }
  return (
    <ul className="m-0 list-none p-0" style={{ fontFamily: 'Inter, sans-serif' }}>
      {items.map((it) => {
        const actions = (it.actions || []).filter(Boolean);
        const busy = actions.some((a) => a.busy);
        return (
          <li
            key={it.id}
            data-active={it.active ? 'true' : undefined}
            className="group relative border-b border-[color:var(--border-subtle)] px-4 py-2.5 last:border-b-0 hover:bg-surface-hover focus-within:bg-surface-hover"
          >
            <div className="flex min-h-[20px] items-center gap-2">
              {onSelect ? (
                <button type="button" onClick={() => onSelect(it)} className="min-w-0 truncate text-left text-[13px] font-medium text-[color:var(--text-primary)] outline-none focus-visible:underline">
                  {it.label}
                </button>
              ) : (
                <span className="min-w-0 truncate text-[13px] font-medium text-[color:var(--text-primary)]">{it.label}</span>
              )}
              {it.live && (
                <span className="shrink-0 text-[11px] font-medium text-[color:var(--trodo-primary)]">{liveLabel}</span>
              )}
              {it.active && !it.live && <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">Viewing</span>}
              {it.meta}
              <span
                className={`ml-auto shrink-0 whitespace-nowrap text-[12px] text-[color:var(--text-muted)] transition-opacity ${actions.length ? 'group-hover:opacity-0 group-focus-within:opacity-0' : ''} ${busy ? 'opacity-0' : ''}`}
              >
                {it.time}
              </span>
            </div>
            {(it.author || it.note) && (
              <div className="mt-0.5 truncate pr-2 text-[12px] text-[color:var(--text-muted)]">
                {[it.author, it.note].filter(Boolean).join(' · ')}
              </div>
            )}
            {it.extra}
            {actions.length > 0 && (
              <div
                className={`absolute right-3 top-2 flex items-center gap-1 transition-opacity focus-within:opacity-100 ${busy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              >
                {actions.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    disabled={a.disabled || a.busy}
                    onClick={a.onClick}
                    title={a.title}
                    aria-label={a.ariaLabel}
                    className="inline-flex h-6 items-center gap-1 rounded px-1.5 text-[12px] font-medium text-[color:var(--text-primary)] outline-none transition-colors hover:bg-[color:var(--border-subtle)] focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-50"
                  >
                    {a.busy && <Loader2 className="h-3 w-3 animate-spin" />}
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
